import { FC, useEffect, useState } from "react";
import { Loader } from "../shared";
import { useNavigate, useParams } from "react-router";
import { DeleteError } from "src/types/deleteError";
import { Tag } from "src/types/tag";
import { useTranslation } from "react-i18next";
import useIsMountedRef from "src/hooks/useIsMountedRef";
import { useSnackbar } from "notistack";
import { Helmet } from "react-helmet-async";
import { Formik } from "formik";
import * as Yup from "yup";
import ChangesFormActions from "../shared/formChanges/ChangesFormActions";
import {
	Box,
	Button,
	Card,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormHelperText,
	Grid,
	Typography,
} from "@mui/material";
import { CustomTextField } from "../shared/CustomTextField";
import { HTTPSTATUSCODES, TagsFieldLimits } from "src/utils/constants";
import { tagService } from "src/services/tags";
import { MenuRoutes } from "src/types/routes";
import UtilsHelper from "src/utils/utils-helper";
import ColorPickerField from "../colorPicker/ColorPickerField";
import DeleteIcon from "@mui/icons-material/Delete";

const AddEditTag: FC = () => {
	const { t } =
		typeof jest !== "undefined" ? { t: (s) => s } : useTranslation();
	const isMountedRef = useIsMountedRef();
	const { enqueueSnackbar } = useSnackbar();
	const navigate = useNavigate();

	const { tagId } = useParams();
	const [tag, setTag] = useState<Tag | null>({} as Tag);

	const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);

	const getTag = async () => {
		if (tagId) {
			try {
				const response = await tagService.getTagById(tagId);

				if (
					isMountedRef.current &&
					response?.status == HTTPSTATUSCODES.StatusCodeSuccess
				) {
					setTag(response.data);
					setIsDataLoaded(true);
				}
			} catch (error) {
				console.error(error);
			}
		}
	};

	useEffect(() => {
		getTag();
	}, [tagId]);

	const handleCancelForm = (
		handleReset: (e: any) => void,
		dirty: boolean
	) => {
		dirty ? handleReset(null) : navigate(MenuRoutes.Tags);
	};

	const addTag = async (newTag: Tag): Promise<void> => {
		setIsSubmitting(true);

		try {
			const response = await tagService.createTag(newTag);

			if (response?.status === HTTPSTATUSCODES.StatusCodeSuccess) {
				enqueueSnackbar(t("Tags.CreateSucceeded"), {
					variant: "success",
				});
				setIsSubmitting(false);
				navigate(MenuRoutes.Tags);
			}
		} catch (error) {
			if (
				error?.response?.status === HTTPSTATUSCODES.StatusCodeConflict
			) {
				enqueueSnackbar(t("Tags.EntityWithSameNameExists"), {
					variant: "error",
				});
			} else {
				enqueueSnackbar(t("Tags.CreateFailed"), { variant: "error" });
			}

			setIsSubmitting(false);
			console.error(error);
		}
	};

	const updateTag = async (updatedTag: Tag): Promise<void> => {
		setIsSubmitting(true);

		try {
			const response = await tagService.updateTag(updatedTag);

			if (response?.status === HTTPSTATUSCODES.StatusCodeSuccess) {
				enqueueSnackbar(t("Tags.UpdateSucceeded"), {
					variant: "success",
				});
				setIsSubmitting(false);
				navigate(MenuRoutes.Tags);
			}
		} catch (error) {
			if (
				error?.response?.status === HTTPSTATUSCODES.StatusCodeConflict
			) {
				enqueueSnackbar(t("Tags.EntityWithSameNameExists"), {
					variant: "error",
				});
			} else if (
				error?.response?.status === HTTPSTATUSCODES.StatusCodeForbidden
			) {
				enqueueSnackbar(t("Tags.EntityDoesNotExist"), {
					variant: "error",
				});
			} else {
				enqueueSnackbar(t("Tags.UpdateFailed"), { variant: "error" });
			}

			setIsSubmitting(false);
			console.error(error);
		}
	};

	const deleteTag = async (): Promise<void> => {
		setIsDeleteDialogOpen(false);
		setIsSubmitting(true);

		try {
			const response = await tagService.deleteTags([tagId]);

			if (response?.status === HTTPSTATUSCODES.StatusCodeSuccess) {
				enqueueSnackbar(t("Tags.DeleteSucceeded"), {
					variant: "success",
				});
				navigate(MenuRoutes.Tags);
			} else {
				enqueueSnackbar(t("Tags.DeleteFailed"), { variant: "error" });
			}
		} catch (error) {
			if (error?.response?.status === HTTPSTATUSCODES.StatusCodeForbidden && error.response.data?.[0]) {
				const responseData = error.response.data[0] as DeleteError[];
				const errorMessage = responseData.map((element) =>
					t("Shared.Errors.MediaAssignedToFlow", {
						name: element.mediaName || tag?.name,
						mediaNames: element.assignedToNames.join(", "),
					})).join("\n");
				enqueueSnackbar(errorMessage, { variant: "error" });
			} else {
				enqueueSnackbar(t("Tags.DeleteFailed"), { variant: "error" });
				console.error(error);
			}
		}

		setIsSubmitting(false);
	};

	return (
		<>
			<Loader isOpen={!(isDataLoaded || !tagId)} isInline={false} />
			<Helmet>
				<title>
					{tagId ? t("Tags.EditTag") : t("Tags.AddTag")} |
					ContactCenter4All
				</title>
			</Helmet>
			{(isDataLoaded || !tagId) && (
				<Card sx={{ pl: 3, pb: 4, pr: 3, pt: 4 }}>
					<Formik
						validateOnMount
						initialValues={{
							name: tagId ? tag?.name : "",
							color: tagId ? tag?.color : "",
							description: tagId ? tag?.description ?? "" : "",
						}}
						validationSchema={Yup.object().shape({
							name: Yup.string()
								.trim()
								.max(
									TagsFieldLimits.NameMaxLength,
									`${t("Tags.NameMaxLengthError")} ${
										TagsFieldLimits.NameMaxLength
									}`
								)
								.required(t("Tags.Required")),
							color: Yup.string()
								.matches(
									UtilsHelper.HexColorRegExp,
									t("Tags.InvalidColor")
								)
								.required(t("Tags.Required")),
							description: Yup.string().max(
								TagsFieldLimits.DescriptionMaxLength,
								`${t("Tags.DescriptionMaxLengthError")} ${
									TagsFieldLimits.DescriptionMaxLength
								}`
							),
						})}
						onSubmit={(values) => {
							const editedTag: Tag = {
								name: values.name.trim(),
								color: values.color,
								description: values.description,
							};

							if (tagId) {
								editedTag.id = tagId;
							}

							tagId ? updateTag(editedTag) : addTag(editedTag);
						}}
					>
						{({
							errors,
							handleBlur,
							handleChange,
							handleSubmit,
							handleReset,
							setFieldValue,
							setFieldTouched,
							touched,
							values,
							dirty,
							isValid,
						}): JSX.Element => (
							<form onSubmit={handleSubmit}>
								<ChangesFormActions
									isValid={true}
									isSubmitting={isSubmitting}
									disableSave={!isValid}
									handleReset={() =>
										handleCancelForm(handleReset, dirty)
									}
									isSaveAction={false}
								/>
								<Grid container spacing={2}>
									<Grid item xs={6}>
										<CustomTextField
											size="small"
											color="info"
											error={Boolean(
												touched.name && errors.name
											)}
											fullWidth
											helperText={
												(touched.name && errors.name) ||
												`${t("Tags.NameHelper")} · ${values.name.length} / ${TagsFieldLimits.NameMaxLength}`
											}
											inputProps={{
												maxLength:
													TagsFieldLimits.NameMaxLength,
											}}
											label={t("Tags.TagName")}
											name="name"
											onBlur={handleBlur}
											onChange={handleChange}
											value={values.name}
											variant="outlined"
											data-testid="name"
										/>
									</Grid>
									<Grid item xs={6}>
										<ColorPickerField
											label={t("Tags.Color")}
											id="tag-color"
											size="small"
											value={values.color}
											onChange={(value) =>
												setFieldValue("color", value)
											}
											onBlur={() =>
												setFieldTouched("color", true)
											}
											error={Boolean(
												touched.color && errors.color
											)}
										/>
											<FormHelperText
												error={Boolean(
													touched.color && errors.color
												)}
											>
												{(touched.color && errors.color) ||
													t("Tags.ColorHelper")}
											</FormHelperText>
									</Grid>
									<Grid item xs={12}>
										<CustomTextField
											size="small"
											color="info"
											error={Boolean(
												touched.description &&
													errors.description
											)}
											fullWidth
											multiline
											minRows={3}
											helperText={
												(touched.description &&
													errors.description) ||
												`${t("Tags.DescriptionHelper")} · ${values.description.length} / ${TagsFieldLimits.DescriptionMaxLength}`
											}
											inputProps={{
												maxLength:
													TagsFieldLimits.DescriptionMaxLength,
											}}
											label={t("Tags.Description")}
											name="description"
											onBlur={handleBlur}
											onChange={handleChange}
											value={values.description}
											variant="outlined"
											data-testid="description"
										/>
									</Grid>
									{tagId && (
										<Grid item xs={12}>
											<Box sx={{ mt: 2 }}>
												<Button
													color="error"
													variant="outlined"
													startIcon={<DeleteIcon />}
													onClick={() =>
														setIsDeleteDialogOpen(
															true
														)
													}
													disabled={isSubmitting}
													data-testid="tag-delete"
												>
													{t("Tags.DeleteTag")}
												</Button>
												<Typography
													variant="body2"
													color="textSecondary"
													sx={{ mt: 1 }}
												>
													{t("Tags.DeleteWarning")}
												</Typography>
											</Box>
										</Grid>
									)}
								</Grid>
							</form>
						)}
					</Formik>
				</Card>
			)}
			<Dialog
				open={isDeleteDialogOpen}
				onClose={() => setIsDeleteDialogOpen(false)}
			>
				<DialogTitle>{t("Tags.DeleteDialogTitle")}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{t("Tags.DeleteDialogText")}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setIsDeleteDialogOpen(false)}
						data-testid="tag-delete-cancel"
					>
						{t("Shared.Cancel")}
					</Button>
					<Button
						onClick={deleteTag}
						color="error"
						data-testid="tag-delete-confirm"
					>
						{t("Tags.Delete")}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default AddEditTag;
